import { adminService } from '@/api/services';
import type { UnwrapData } from '@/api/types';
import type { ETestStatus } from '@/shared/types';

import type {
  ITestingParticipantResponse,
  IAdmissionTestingResponse,
  ISendTestResultsResponse,
  ITestingParticipantsResponse,
} from './types';

export const admissionTestsService = adminService.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Получить список участников тестирования
     */
    getTestingParticipants: builder.query<
      ITestingParticipantsResponse,
      { schoolId: number; page?: number; perPage?: number; statusFilter?: ETestStatus[] | null }
    >({
      query: ({ schoolId, page, perPage, statusFilter }) => ({
        method: 'GET',
        url: `/schools/${schoolId}/admission_exams`,
        params: {
          page,
          per_page: perPage,
          q: {
            status_in: statusFilter,
          },
        },
      }),
      providesTags: (_, __, { schoolId }) => [{ type: 'SchoolTestingParticipants', id: schoolId }],
    }),
    /**
     * Получить данные участника тестирования
     */
    getTestingParticipant: builder.query<
      UnwrapData<ITestingParticipantResponse>,
      { schoolId: number; participantId: number }
    >({
      query: ({ schoolId, participantId }) => ({
        method: 'GET',
        url: `/schools/${schoolId}/admission_exams/${participantId}`,
      }),
      transformResponse: (response: ITestingParticipantResponse) => response.data,
    }),
    /**
     * Одобрить участника тестирования для дальнейшего обучения
     */
    approveTestingParticipant: builder.mutation<
      null,
      { schoolId: number; participantId: number; approvalComment: string | null }
    >({
      query: ({ schoolId, participantId, approvalComment }) => ({
        method: 'POST',
        url: `/schools/${schoolId}/admission_exams/${participantId}/approve`,
        body: {
          data: {
            approval_comment: approvalComment,
          },
        },
      }),
      invalidatesTags: (_, __, { schoolId }) => [
        { type: 'SchoolTestingParticipants', id: schoolId },
        { type: 'SchoolAdmissionTesting', id: schoolId },
      ],
    }),
    /**
     * Отклонить участника тестирования для дальнейшего обучения
     */
    rejectTestingParticipant: builder.mutation<
      null,
      { schoolId: number; participantId: number; rejectionReason: string; isUserBlocked: boolean }
    >({
      query: ({ schoolId, participantId, rejectionReason, isUserBlocked }) => ({
        method: 'POST',
        url: `/schools/${schoolId}/admission_exams/${participantId}/reject`,
        body: {
          data: {
            rejection_reason: rejectionReason,
            block_user: isUserBlocked,
          },
        },
      }),
      invalidatesTags: (_, __, { schoolId }) => [
        { type: 'SchoolTestingParticipants', id: schoolId },
        { type: 'SchoolAdmissionTesting', id: schoolId },
      ],
    }),
    /**
     * Перевод из резерва в основной список
     */
    activateFromReserve: builder.mutation<null, { schoolId: number; reservedUsersIds: number[] }>({
      query: ({ schoolId, reservedUsersIds }) => ({
        method: 'POST',
        url: `/schools/${schoolId}/admission_testing/activate_from_reserve`,
        body: {
          data: {
            exam_ids: reservedUsersIds,
          },
        },
      }),
      invalidatesTags: (_, __, { schoolId }) => [
        { type: 'SchoolTestingParticipants', id: schoolId },
        { type: 'SchoolAdmissionTesting', id: schoolId },
      ],
    }),
    /**
     * Привязать тест к школе
     */
    attachTest: builder.mutation<null, { schoolId: number; lmsCourseId: number }>({
      query: ({ schoolId, lmsCourseId }) => ({
        method: 'POST',
        url: `/schools/${schoolId}/admission_testing/link_test`,
        body: {
          data: {
            lms_course_id: lmsCourseId,
          },
        },
      }),
      invalidatesTags: (_, __, { schoolId }) => [{ type: 'SchoolAdmissionTesting', id: schoolId }],
    }),
    /**
     * Получить данные этапа тестирования
     */
    getAdmissionTesting: builder.query<UnwrapData<IAdmissionTestingResponse>, { schoolId: number }>({
      query: ({ schoolId }) => ({
        method: 'GET',
        url: `/schools/${schoolId}/admission_testing`,
      }),
      providesTags: (_, __, { schoolId }) => [{ type: 'SchoolAdmissionTesting', id: schoolId }],
      transformResponse: (response: IAdmissionTestingResponse) => response.data,
    }),
    /**
     * Отправить результаты тестирования
     */
    sendTestResults: builder.mutation<ISendTestResultsResponse, { schoolId: number }>({
      query: ({ schoolId }) => ({
        method: 'POST',
        url: `/schools/${schoolId}/admission_testing/notify_results`,
      }),
      invalidatesTags: (_, __, { schoolId }) => [
        { type: 'SchoolTestingParticipants', id: schoolId },
        { type: 'SchoolAdmissionTesting', id: schoolId },
      ],
    }),
    /**
     * Отправить результаты тестирования и распределить пользователей
     */
    distributeTestResults: builder.mutation<null, { schoolId: number; mainListExamIds: number[] }>({
      query: ({ schoolId, mainListExamIds }) => ({
        method: 'POST',
        url: `/schools/${schoolId}/admission_testing/distribute_results`,
        body: {
          data: {
            main_list_exam_ids: mainListExamIds,
          },
        },
      }),
      invalidatesTags: (_, __, { schoolId }) => [
        { type: 'SchoolTestingParticipants', id: schoolId },
        { type: 'SchoolAdmissionTesting', id: schoolId },
      ],
    }),
    /**
     * Отправить письма напоминания не приступившим пользователям
     */
    sendReminderLetters: builder.mutation<null, { schoolId: number }>({
      query: ({ schoolId }) => ({
        method: 'POST',
        url: `/schools/${schoolId}/admission_testing/remind_not_started`,
      }),
    }),
    /**
     * Отправить финальные решения
     */
    sendDecisions: builder.mutation<null, { schoolId: number }>({
      query: ({ schoolId }) => ({
        method: 'POST',
        url: `/schools/${schoolId}/admission_testing/notify_decisions`,
      }),
      invalidatesTags: (_, __, { schoolId }) => [
        { type: 'SchoolTestingParticipants', id: schoolId },
        { type: 'SchoolAdmissionTesting', id: schoolId },
      ],
    }),
  }),
});

export const {
  useGetTestingParticipantsQuery,
  useGetTestingParticipantQuery,
  useApproveTestingParticipantMutation,
  useRejectTestingParticipantMutation,
  useActivateFromReserveMutation,
  useAttachTestMutation,
  useGetAdmissionTestingQuery,
  useSendTestResultsMutation,
  useDistributeTestResultsMutation,
  useSendReminderLettersMutation,
  useSendDecisionsMutation,
} = admissionTestsService;
